/*
 * drawer-builder-color-chip.js  (segmented filter control)
 * --------------------------------------------------------------------------
 * Mobile filter UI = a full-width segmented control aligned to the product-tile
 * grid: Colour | Type | Size | Utensil (equal 1fr segments, icon-only — the
 * axis name rides on aria-label/title). Only one sheet is open at a time. Tapping a
 * segment opens a bottom sheet for that axis:
 *   - Type/Size/Utensil  -> proxy-click the real (hidden) chip-rail axis button,
 *     reusing the existing refine sheet in drawer-filter-rehaul.js.
 *   - Colour             -> a matching bottom sheet of swatches that proxies the
 *     canonical `.colorsOption span` controls.
 *
 * This is an isolated overlay: it hides the legacy `.db-chip-rail` + `.variantData`
 * and builds its own UI, WITHOUT editing the fragile filter engine. Remove the
 * <script> tag + this file to fully revert.
 *
 * Default ON in the sheet band only (<=999px = MD_CONST.MOBILE_BREAKPOINT, so
 * phones AND tablets) — this is now the standard mobile filter UI, so the old
 * colour swatch row stays hidden there (no wasted space).
 * Opt out for testing/rollback with ?colorchip=0 (sticky in localStorage);
 * re-enable with ?colorchip=1. Desktop is unchanged (legacy controls).
 * --------------------------------------------------------------------------
 */
(function () {
  'use strict';
  var FLAG = 'md-colorchip';
  var FF = "'Montserrat',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";
  // Single source of truth for the mobile breakpoint (shared via MD_CONST, with
  // a literal fallback per the constants-file contract — keep it EQUAL to the
  // constant). NOTE: the CSS `@media (max-width: 999px)` sheet-band rules must
  // be kept in sync with this value.
  var MOBILE_MAX = (window.MD_CONST && window.MD_CONST.MOBILE_BREAKPOINT) || 999;

  var params = new URLSearchParams(window.location.search);
  if (params.has('colorchip')) {
    var v = params.get('colorchip');
    var off = (v === '0' || v === 'off' || v === 'false');
    // Store an explicit on/off so a prior opt-out sticks; default is ON.
    try { localStorage.setItem(FLAG, off ? '0' : '1'); } catch (e) {}
  }
  function isOn() { try { return localStorage.getItem(FLAG) !== '0'; } catch (e) { return true; } }
  function ready(fn) { document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn) : fn(); }

  var ICON = {
    type: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3" y="3" width="6" height="6" rx="1.5"/><rect x="11" y="3" width="6" height="6" rx="1.5"/><rect x="3" y="11" width="6" height="6" rx="1.5"/><rect x="11" y="11" width="6" height="6" rx="1.5"/></svg>',
    size: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="14" height="14" rx="2"/><path d="M7 13 L13 7"/><path d="M13 7 L9.5 7 M13 7 L13 10.5"/></svg>',
    utensil: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3.2 8.4V4.2a1 1 0 0 1 1-1h4.2a1 1 0 0 1 .7.3l7.4 7.4a1 1 0 0 1 0 1.4l-4.2 4.2a1 1 0 0 1-1.4 0L3.5 9.1a1 1 0 0 1-.3-.7Z"/><circle cx="6.6" cy="6.6" r="1.05"/></svg>',
    clear: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M5.5 5.5 L14.5 14.5 M14.5 5.5 L5.5 14.5"/></svg>',
    // Fallback glyph for any axis derived from the rail that lacks a bespoke
    // icon (so a newly-added filter axis still renders a tile).
    fallback: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="10" r="6.5"/><path d="M10 7v6 M7 10h6"/></svg>'
  };

  // Derive the filter axes from the server-rendered chip rail — the SAME markup
  // the rehaul engine wires — so this is a thin skin over that one engine rather
  // than a separate hard-coded list. Colour is always the first tile (driven by
  // the swatch sheet, not the rail). To add a filter axis you now only add a
  // [data-db-axis] chip to the rail markup + the rehaul AXES; the mobile tiles
  // adapt automatically (count, labels, and grid columns).
  function deriveAxes(rail) {
    var axes = [{ seg: 'colour', label: 'Colour' }];
    Array.prototype.slice.call(rail.querySelectorAll('[data-db-axis]')).forEach(function (btn) {
      var seg = btn.getAttribute('data-db-axis');
      if (!seg) return;
      var lbl = btn.querySelector('.db-chip__label');
      axes.push({ seg: seg, label: (lbl ? lbl.textContent : (btn.textContent || seg)).trim() });
    });
    return axes;
  }

  function injectStyles() {
    if (document.getElementById('md-colorchip-style')) return;
    var css = ''
      // bar matches product-tile width (panel already pads 16px each side, so no extra inset here)
      + '.db-segwrap{display:none;font-family:' + FF + ';padding:2px 0 6px;width:100%;max-width:100%;align-self:stretch;box-sizing:border-box;overflow:hidden;}'
      + 'body.md-colorchip-on.products-expanded .db-segwrap{display:block;}'
      + '.db-seg{display:grid;grid-template-columns:repeat(var(--db-seg-n,4),minmax(0,1fr));column-gap:var(--db-seg-gap,3px);width:100%;box-sizing:border-box;background:#eef0f2;border-radius:13px;padding:3px;}'
      // Responsive sizing knobs — every dimension below reads one of these vars so
      // the bar scales together at the breakpoints near the bottom of this string.
      + '.db-seg{--db-item-h:46px;--db-icon:22px;--db-badge-sz:15px;--db-badge-off:14px;--db-seg-gap:3px;--db-clear-pad:15px;}'
      // a compact clear cell appears at the end only when a filter is active (no separate row)
      + '.db-seg.is-dirty{grid-template-columns:repeat(var(--db-seg-n,4),minmax(0,1fr)) auto;}'
      // Icon-only segments (labels removed for a clean bar; aria-label/title carry
      // the name for accessibility + tooltip). Centred icon, no text row.
      + '.db-seg__item{position:relative;min-width:0;display:flex;align-items:center;justify-content:center;height:var(--db-item-h,46px);border:0;background:none;border-radius:10px;cursor:pointer;color:#5b6068;-webkit-tap-highlight-color:transparent;}'
      + '.db-seg__clear{display:none;min-width:0;align-items:center;justify-content:center;height:var(--db-item-h,46px);padding:0 var(--db-clear-pad,15px);border:0;border-left:1px solid #d9dbe0;background:none;border-radius:0 10px 10px 0;cursor:pointer;color:#9aa0a8;-webkit-tap-highlight-color:transparent;}'
      // Defend the segment buttons against theme/app global <button> styles
      // (e.g. padding:0 40px + content-box) bleeding in and inflating them past
      // their grid cell — which made the active white pills paint wider than
      // their 1fr track and overlap neighbours. Pin the box model + padding so
      // the cells stay exactly 1fr regardless of any external button rule.
      + '.db-seg__item,.db-seg__clear{box-sizing:border-box!important;min-width:0!important;margin:0!important;}'
      + '.db-seg__item{padding:0!important;}'
      + '.db-seg__clear{padding:0 var(--db-clear-pad,15px)!important;}'
      + '.db-seg.is-dirty .db-seg__clear{display:flex;}'
      + '.db-seg__clear:active{color:#5b6068;}'
      + '.db-seg__clear .db-seg__icon{width:calc(var(--db-icon,22px) - 4px);height:calc(var(--db-icon,22px) - 4px);}'
      + '.db-seg__clear .db-seg__icon svg{width:calc(var(--db-icon,22px) - 4px);height:calc(var(--db-icon,22px) - 4px);}'
      + '.db-seg__item:not(:first-child)::before{content:"";position:absolute;left:0;top:12px;bottom:12px;width:1px;background:#d9dde2;}'
      + '.db-seg__item.is-active{background:#fff;color:#16181d;box-shadow:0 1px 3px rgba(0,0,0,.12);}'
      + '.db-seg__item.is-active::before,.db-seg__item.is-active+.db-seg__item::before{display:none;}'
      + '.db-seg__icon{width:var(--db-icon,22px);height:var(--db-icon,22px);display:flex;align-items:center;justify-content:center;}'
      + '.db-seg__icon svg{width:var(--db-icon,22px);height:var(--db-icon,22px);}'
      + '.db-seg__swatch{width:calc(var(--db-icon,22px) - 2px);height:calc(var(--db-icon,22px) - 2px);border-radius:50%;border:1px solid rgba(0,0,0,.15);box-shadow:inset 0 0 0 1.5px #fff;}'
      // Anchor the badge to the icon's top-right corner (offset from centre), and
      // size it from a var, so it hugs the glyph and tightens on narrow phones
      // instead of drifting into the neighbouring segment. pointer-events:none
      // keeps the proxy-click (which targets .db-seg__item) unaffected.
      + '.db-seg__badge{position:absolute;top:5px;left:50%;transform:translateX(calc(var(--db-badge-off,14px) - 50%));min-width:var(--db-badge-sz,15px);height:var(--db-badge-sz,15px);border-radius:999px;background:#3a3af5;color:#fff;font:800 9px/var(--db-badge-sz,15px) ' + FF + ';text-align:center;padding:0 3px;box-sizing:border-box;display:none;pointer-events:none;}'
      // Responsive ladder — shrink the bar on smaller phones so four icons + the
      // badges + the clear column never crowd or overlap. Component is JS-gated to
      // <=999px (the sheet band), so the base values above serve tablets and the
      // largest phones.
      + '@media (max-width:390px){.db-seg{--db-item-h:44px;--db-icon:21px;--db-badge-sz:14px;--db-badge-off:13px;--db-clear-pad:13px;}}'
      + '@media (max-width:360px){.db-seg{--db-item-h:42px;--db-icon:20px;--db-badge-sz:14px;--db-badge-off:12px;--db-clear-pad:11px;--db-seg-gap:2px;}}'
      + '@media (max-width:340px){.db-seg{--db-item-h:40px;--db-icon:19px;--db-badge-sz:13px;--db-badge-off:11px;--db-clear-pad:9px;--db-seg-gap:2px;}}'
      // colour bottom sheet
      + '.db-csheet-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.35);z-index:2147483646;opacity:0;transition:opacity .2s;}'
      + '.db-csheet-backdrop.is-open{opacity:1;}'
      + '.db-csheet{position:fixed;left:0;right:0;bottom:0;z-index:2147483647;background:#fff;border-radius:18px 18px 0 0;padding:10px 16px calc(18px + env(safe-area-inset-bottom,0px));transform:translateY(101%);transition:transform .25s ease;max-height:72vh;overflow:auto;-webkit-overflow-scrolling:touch;font-family:' + FF + ';box-shadow:0 -8px 30px rgba(0,0,0,.18);}'
      + '.db-csheet.is-open{transform:translateY(0);}'
      + '.db-csheet__grab{width:40px;height:4px;border-radius:2px;background:#d8d8d8;margin:2px auto 12px;}'
      + '.db-csheet__title{font:800 16px ' + FF + ';margin:0 0 12px;color:#16181d;}'
      + '.db-csheet__grid{display:grid;grid-template-columns:repeat(2,1fr);gap:8px;}'
      + '.db-csheet__opt{display:flex;align-items:center;gap:12px;padding:12px;border:1px solid #e1e1e1;border-radius:12px;background:#fff;font:600 14px ' + FF + ';color:#16181d;cursor:pointer;text-align:left;}'
      + '.db-csheet__opt.is-active{border-color:#3a3af5;background:#f4f5ff;}'
      + '.db-csheet__opt.is-active .db-csheet__chk{margin-left:auto;color:#3a3af5;font-weight:800;}'
      + '.db-csheet__dot{width:22px;height:22px;border-radius:50%;border:1px solid rgba(0,0,0,.15);box-shadow:inset 0 0 0 1.5px #fff;flex:0 0 auto;}'
      // swipe-up hint (collapsed peek)
      + '.db-swipe-hint{display:none;align-items:center;justify-content:center;gap:5px;width:100%;border:0;background:none;cursor:pointer;font-family:' + FF + ';font-size:10.5px;font-weight:600;letter-spacing:.01em;color:#9a9a9a;padding:0 12px 1px;line-height:1;min-height:0;}'
      + '.db-swipe-hint__chev{font-size:12px;font-weight:800;color:#3a3af5;line-height:1;transform:translateY(1px);}'
      + 'body.md-colorchip-on:not(.products-expanded) .db-swipe-hint{display:flex;}'
      // Force the slim height: a generic button/touch-target rule (or flex
      // stretch from .builtbundle) was inflating it to ~48px despite min-height:0.
      // High specificity + !important + flex:0 0 auto wins and kills any stretch.
      + 'body.md-colorchip-on .builtbundle button.db-swipe-hint{height:20px !important;min-height:0 !important;max-height:20px !important;flex:0 0 auto !important;align-self:center !important;line-height:1 !important;padding:0 12px !important;}'
      + 'body.md-colorchip-on .variantData{display:none !important;}';
    var s = document.createElement('style');
    s.id = 'md-colorchip-style';
    s.textContent = css;
    document.head.appendChild(s);
  }

  // Collapsed peek hint
  function addSwipeHint() {
    if (document.querySelector('.db-swipe-hint')) return;
    var host = document.querySelector('.builtbundle');
    if (!host) return;
    var hint = document.createElement('button');
    hint.type = 'button';
    hint.className = 'db-swipe-hint';
    hint.innerHTML = '<span class="db-swipe-hint__chev" aria-hidden="true">⌃</span><span>Swipe up to browse all modules</span>';
    hint.addEventListener('click', function () {
      var g = document.getElementById('db-sheet-grabber') || document.querySelector('.db-sheet-grabber');
      if (g) g.click();
    });
    host.insertBefore(hint, host.firstChild);
  }

  ready(function () {
    if (!isOn()) return;
    if (!document.body.classList.contains('drawer-builder')) return;
    var mobileMedia = window.matchMedia('(max-width: ' + MOBILE_MAX + 'px)');

    var wrapper = document.querySelector('.product-filter-select-wrapper');
    var rail = document.querySelector('.db-chip-rail');
    if (!wrapper || !rail) return;

    // Filter axes derived from the rail (single source of truth — see deriveAxes).
    var AXES = deriveAxes(rail);

    injectStyles();
    var colourRows = Array.prototype.slice.call(document.querySelectorAll('.variantData'));
    var overlayHidden = false;
    function showNativeControls(el) {
      // Remove only the hide installed by this overlay. The CSS can then
      // restore the desktop rail/swatches at the shared 1000px boundary.
      if (el.style.getPropertyValue('display') === 'none' &&
          el.style.getPropertyPriority('display') === 'important') {
        el.style.removeProperty('display');
      }
    }

    // ---- colour source-of-truth helpers (canonical = .colorsOption span) ----
    function sources() { return Array.prototype.slice.call(document.querySelectorAll('.colorsOption span')); }
    function activeSwatch() { return document.querySelector('.colorsOption span.active') || sources()[0] || null; }
    function swatchHex(el) { return (el && (el.getAttribute('data-hex') || el.style.backgroundColor)) || '#000'; }
    function swatchName(el) { return (el && (el.getAttribute('data-text') || el.getAttribute('title'))) || 'Colour'; }

    // ---- build segmented control ----
    var seg = document.createElement('div');
    seg.className = 'db-segwrap';
    seg.innerHTML =
        '<div class="db-seg" role="tablist">'
      +   AXES.map(function (a) {
            var glyph = a.seg === 'colour'
              ? '<span class="db-seg__swatch" data-seg-swatch></span>'
              : (ICON[a.seg] || ICON.fallback);
            return '<button type="button" class="db-seg__item" data-seg="' + a.seg + '" role="tab" aria-label="' + a.label + '" title="' + a.label + '">'
              + '<span class="db-seg__icon">' + glyph + '</span>'
              + '<span class="db-seg__badge"></span></button>';
          }).join('')
      +   '<button type="button" class="db-seg__clear" data-seg-clear aria-label="Clear all filters" title="Clear all filters">'
      +     '<span class="db-seg__icon">' + ICON.clear + '</span></button>'
      + '</div>';
    wrapper.insertBefore(seg, wrapper.firstChild);

    var clearEl = seg.querySelector('[data-seg-clear]');
    var segGrid = seg.querySelector('.db-seg');
    // Drive the grid column count from the derived axis count (stays 4 today;
    // adapts automatically if an axis is added).
    segGrid.style.setProperty('--db-seg-n', String(AXES.length));

    // Only one sheet open at a time: close the rehaul refine sheet if it's up.
    function closeRefineSheetIfOpen() {
      if (typeof window.MD_closeFilterUI === 'function') { window.MD_closeFilterUI(); return; }
      var rs = document.querySelector('[data-db-refine-sheet]');
      if (!rs) return;
      if (rs.classList.contains('is-open') || rs.hidden === false) {
        var cb = rs.querySelector('[data-db-refine-close]');
        if (cb) { cb.click(); return; }
        rs.classList.remove('is-open');
        rs.hidden = true;
        document.body.classList.remove('db-refine-open');
      }
    }

    // ---- proxy actions to the (hidden) real controls ----
    seg.querySelector('.db-seg').addEventListener('click', function (e) {
      var item = e.target.closest('.db-seg__item');
      if (!item) return;
      var which = item.getAttribute('data-seg');
      if (which === 'colour') {
        if (sheet) { closeColourSheet(); return; }
        closeRefineSheetIfOpen();
        openColourSheet();
        return;
      }
      closeColourSheet(); // never stack our colour sheet under the refine sheet
      var real = rail.querySelector('[data-db-axis="' + which + '"]');
      if (real) real.click(); // rehaul opens its refine sheet
    });
    clearEl.addEventListener('click', function () {
      var cl = rail.querySelector('[data-db-clear]');
      if (cl) cl.click();
      setTimeout(sync, 0);
    });

    // ---- colour bottom sheet ----
    var backdrop = null, sheet = null;
    function closeColourSheet() {
      if (!sheet && !backdrop) return;
      document.removeEventListener('click', dismissColourOutside, true);
      document.removeEventListener('keydown', dismissColourEscape);
      if (sheet) sheet.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      var bd = backdrop, sh = sheet; backdrop = sheet = null;
      setTimeout(function () { if (bd) bd.remove(); if (sh) sh.remove(); }, 260);
    }
    function dismissColourOutside(e) {
      if (sheet && !sheet.contains(e.target) && !e.target.closest('.db-seg__item')) closeColourSheet();
    }
    function dismissColourEscape(e) {
      if (e.key === 'Escape') closeColourSheet();
    }
    window.MD_closeColourSheet = closeColourSheet;
    function openColourSheet() {
      if (sheet) return;
      backdrop = document.createElement('div');
      // The theme hides empty divs unless they carry its no-empty exemption.
      backdrop.className = 'db-csheet-backdrop no-empty';
      backdrop.addEventListener('click', closeColourSheet);
      sheet = document.createElement('div');
      sheet.className = 'db-csheet';
      var grid = sources().map(function (src) {
        var hex = swatchHex(src), name = swatchName(src), on = src.classList.contains('active');
        return '<button type="button" class="db-csheet__opt' + (on ? ' is-active' : '') + '" data-id="' + (src.getAttribute('data-id') || '') + '">'
          + '<span class="db-csheet__dot" style="background:' + hex + '"></span>'
          + '<span>' + name + '</span>' + (on ? '<span class="db-csheet__chk">✓</span>' : '') + '</button>';
      }).join('');
      sheet.innerHTML = '<div class="db-csheet__grab"></div><div class="db-csheet__title">Colour</div><div class="db-csheet__grid">' + grid + '</div>';
      sheet.addEventListener('click', function (e) {
        var opt = e.target.closest('.db-csheet__opt');
        if (!opt) return;
        var id = opt.getAttribute('data-id');
        var src = sources().filter(function (s) { return (s.getAttribute('data-id') || '') === id; })[0];
        if (src) src.click(); // canonical colour change (image swap + filter)
        setTimeout(sync, 0);
        closeColourSheet();
      });
      document.body.appendChild(backdrop);
      document.body.appendChild(sheet);
      document.addEventListener('click', dismissColourOutside, true);
      document.addEventListener('keydown', dismissColourEscape);
      var bd = backdrop, sh = sheet;
      requestAnimationFrame(function () { requestAnimationFrame(function () {
        if (backdrop !== bd || sheet !== sh) return;
        bd.classList.add('is-open');
        sh.classList.add('is-open');
      }); });
    }

    // ---- keep segments in sync with the engine ----
    function sync() {
      var anyActive = false;
      AXES.forEach(function (a) {
        var item = seg.querySelector('[data-seg="' + a.seg + '"]');
        if (!item) return;
        if (a.seg === 'colour') {
          var sw = item.querySelector('[data-seg-swatch]');
          var act = activeSwatch();
          if (sw && act) sw.style.background = swatchHex(act);
          return;
        }
        var real = rail.querySelector('[data-db-axis="' + a.seg + '"]');
        var count = 0, active = false;
        if (real) {
          active = real.classList.contains('is-active');
          var lbl = real.querySelector('.db-chip__label');
          var m = (lbl ? lbl.textContent : '').match(/(\d+)/);
          count = m ? parseInt(m[1], 10) : 0;
        }
        item.classList.toggle('is-active', active);
        if (active) anyActive = true;
        var badge = item.querySelector('.db-seg__badge');
        if (badge) { badge.textContent = count || ''; badge.style.display = count > 0 ? 'block' : 'none'; }
        var selectedText = count > 0 && window.FoxTheme && window.FoxTheme.i18n &&
          typeof window.FoxTheme.i18n.t === 'function'
          ? window.FoxTheme.i18n.t('modu.builder.selected_count', { count: count }) : '';
        item.setAttribute('aria-label', a.label + (selectedText ? ', ' + selectedText : ''));
      });
      segGrid.classList.toggle('is-dirty', anyActive);
    }

    if (window.MutationObserver) {
      new MutationObserver(sync).observe(rail, { attributes: true, childList: true, subtree: true });
      var co = document.querySelector('.colorsOption');
      if (co) new MutationObserver(sync).observe(co, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }

    addSwipeHint();
    function syncPresentation() {
      var mobile = mobileMedia.matches;
      document.body.classList.toggle('md-colorchip-on', mobile);
      if (mobile) {
        rail.style.setProperty('display', 'none', 'important');
        colourRows.forEach(function (el) { el.style.setProperty('display', 'none', 'important'); });
        overlayHidden = true;
      } else if (overlayHidden) {
        showNativeControls(rail);
        colourRows.forEach(showNativeControls);
        closeColourSheet();
        closeRefineSheetIfOpen();
        overlayHidden = false;
      }
      sync();
    }
    if (mobileMedia.addEventListener) mobileMedia.addEventListener('change', syncPresentation);
    else if (mobileMedia.addListener) mobileMedia.addListener(syncPresentation);
    syncPresentation();
  });
})();
